import React from "react";
import { useDrop } from "react-dnd";
import styled, { css } from "styled-components";

import { Stem } from "@examspark/types";

import Mark from "../../common/Mark";
import { DroppableType } from "../common/image-map/ImageMap";

import GraphicGapToken from "./GraphicGapToken";
import Token, { DndItem } from "./Token";

const Gap = styled.span<{
  isOver: boolean;
  hasMultipleTokens: boolean;
  flowDirection: "vertical" | "horizontal";
}>`
  background-color: transparent;
  border: dashed 2px #ccc;
  min-height: 2rem;
  width: 5rem;
  transition: border-color 0.2s;
  display: inline-block;
  vertical-align: middle;
  position: absolute;

  ${({ isOver }) =>
    isOver &&
    css`
      border: dashed 2px #006699;
    `}

  ${({ flowDirection, hasMultipleTokens }) =>
    !hasMultipleTokens
      ? css`
          display: flex;
          justify-content: center;
          align-items: center;
        `
      : flowDirection === "vertical"
        ? css`
            flex-direction: column-reverse;
            display: flex;
            justify-content: end;
            align-items: center;
          `
        : css`
            flex-direction: row;
            display: flex;
            justify-content: start;
            align-items: center;
          `}

  & .markup {
    position: absolute;
    right: -1rem;
    top: -1rem;

    z-index: 1;
  }
`;

type GraphicGapProps = {
  gapId: string;
  mark?: "correct" | "incorrect";
  dropped?: DroppableType[];
  allowMultipleTokens?: boolean;

  coords?: Stem.Coordinates;

  onDrop: (gapId: string, item: DndItem) => void;
  onTokenRemove: (gapId: string, item: DndItem) => void;
};

const GraphicGap = (props: GraphicGapProps) => {
  const [{ isOver }, drop] = useDrop<DndItem, void, { isOver: boolean }>({
    accept: "token",
    drop: (item) => {
      props.onDrop(props.gapId, item);
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  });

  const handleTokenRemove = (item: DndItem) => {
    props.onTokenRemove(props.gapId, item);
  };

  return (
    <Gap
      data-cy={"graphic-gap"}
      ref={drop}
      isOver={isOver}
      hasMultipleTokens={!!props.allowMultipleTokens}
      flowDirection={
        (props.coords?.w ?? 0) > (props.coords?.h ?? 0)
          ? "horizontal"
          : "vertical"
      }
      style={{
        left: props.coords?.x,
        top: props.coords?.y,
        width: props.coords?.w,
        height: props.coords?.h,
      }}
    >
      {props.mark && <Mark type={props.mark} />}

      {props.dropped?.map((drop) => {
        if (drop.type === "text") {
          return (
            <Token
              key={drop.drop?.instanceId}
              data={drop.item}
              useDragType="token"
              gapId={props.gapId}
              instanceId={drop.drop?.instanceId}
              onEnd={(item, monitor) => {
                // remove the token if it was dropped into whitespace
                if (!monitor.didDrop() && item) {
                  handleTokenRemove(item);
                }
              }}
            />
          );
        } else if (drop.type === "graphic") {
          return (
            <GraphicGapToken
              key={drop.drop?.instanceId}
              data={drop.item}
              useDragType="token"
              allowMultipleTokens={props.allowMultipleTokens}
              gapId={props.gapId}
              instanceId={drop.drop?.instanceId}
              onEnd={(item, monitor) => {
                // remove the token if it was dropped into whitespace
                if (!monitor.didDrop() && item) {
                  handleTokenRemove(item);
                }
              }}
            />
          );
        } else {
          return null;
        }
      })}
    </Gap>
  );
};
export default GraphicGap;
